import { Badge } from "src/components/ui/Badge";
import { cn } from "src/utils";
import { Resignation } from "../resignation.types";
import { formatDate, formatDateLong, formatDateTime, formatWeekday, getNoticeProgress, getStatusStyle, titleCase } from "../utils";
import { DialogDescription, DialogHeader, DialogTitle } from "src/components/dialogue/radix-dialog";
import { Avatar } from "..";

export function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full px-3 py-0.5 font-semibold",
        getStatusStyle(status),
      )}
    >
      {titleCase(status)}
    </Badge>
  );
}

function DetailItem({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 font-medium">{children}</dd>
    </div>
  );
}


const ResignationDetails = ({ request }: { request: Resignation }) => {
  const { employee } = request;
  const { progress, label } = getNoticeProgress(
    request.resignation_date,
    request.last_working_day,
  );

  return (
    <>
      <DialogHeader className="text-left">
        <div className="flex items-center justify-between gap-3 pr-6">
          <DialogTitle>Resignation request</DialogTitle>
          <StatusBadge status={request.status} />
        </div>
        <DialogDescription>
          Reference {request.id.slice(0, 8).toUpperCase()}
        </DialogDescription>
      </DialogHeader>

      {/* Employee */}
      <section aria-label="Employee" className="mt-5 rounded-2xl border p-4">
        <div className="flex items-center gap-4">
          <Avatar name={employee.full_name} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold">
              {employee.full_name}
            </p>
            <p className="truncate text-sm text-muted-foreground">
              {employee.designation}, {employee.department}
            </p>
            <p className="text-sm text-muted-foreground">
              {employee.employee_code}
            </p>
          </div>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-4 border-t pt-4 sm:grid-cols-2">
          <DetailItem label="Role">{titleCase(employee.role)}</DetailItem>
          <DetailItem label="Phone">
            <a
              href={`tel:${employee.phone_number}`}
              className="text-primary hover:underline"
            >
              {employee.phone_number}
            </a>
          </DetailItem>
          <DetailItem label="Email" className="sm:col-span-2">
            <a
              href={`mailto:${employee.email}`}
              className="break-all text-primary hover:underline"
            >
              {employee.email}
            </a>
          </DetailItem>
        </dl>
      </section>

      {/* Last working day */}
      <section
        aria-label="Notice period"
        className="mt-4 rounded-2xl bg-gradient-to-b from-primary/10 to-background p-5"
      >
        <p className="text-sm font-medium text-primary">Last working day</p>
        <p className="mt-2 font-serif text-3xl font-semibold leading-tight tracking-tight">
          {formatDateLong(request.last_working_day)}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {formatWeekday(request.last_working_day)}
          <span className="mx-2" aria-hidden="true">
            &middot;
          </span>
          <span className="font-medium text-primary">{label}</span>
        </p>

        <div className="mt-5">
          <div
            role="progressbar"
            aria-label="Notice period progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="h-2 overflow-hidden rounded-full bg-primary/15"
          >
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between text-xs text-muted-foreground">
            <span>{formatDate(request.resignation_date)}</span>
            <span>{formatDate(request.last_working_day)}</span>
          </div>
        </div>
      </section>

      {/* Resignation details */}
      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-5">
        <DetailItem label="Resignation date">
          {formatDate(request.resignation_date)}
        </DetailItem>
        <DetailItem label="Notice period">
          {request.notice_days} days
        </DetailItem>
        <DetailItem label="Reason">{request.reason}</DetailItem>
        <DetailItem label="Notice period waived">
          {request.waive_notice_period ? "Yes" : "No"}
        </DetailItem>
        <DetailItem label="Submitted on">
          {formatDateTime(request.created_at)}
        </DetailItem>
        <DetailItem label="Last updated">
          {formatDateTime(request.updated_at)}
        </DetailItem>
        <DetailItem label="Request ID" className="col-span-2">
          <span className="break-all font-mono text-sm">{request.id}</span>
        </DetailItem>
      </dl>
    </>
  );
};

export default ResignationDetails;
